import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { mediaUrl, trilhaJson } from './api';

const TIPO_ICON = {
  video: <VideocamIcon />,
  imagem: <ImageIcon />,
  pdf: <PictureAsPdfIcon />,
  ppt: <SlideshowIcon />,
  arquivo: <InsertDriveFileIcon />,
};

const TIPO_COLOR = {
  video: '#1565c0',
  imagem: '#6a1b9a',
  pdf: '#c62828',
  ppt: '#ef6c00',
  arquivo: '#546e7a',
};

export default function TrilhaConteudosPage() {
  const { pastaId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pasta, setPasta] = useState(null);
  const [setor, setSetor] = useState(null);
  const [conteudos, setConteudos] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState('');
  const [arquivo, setArquivo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewer, setViewer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await trilhaJson(`/api/trilha-aprendizado/pastas/${pastaId}/conteudos`);
      setPasta(data.pasta || null);
      setSetor(data.setor || null);
      setConteudos(data.conteudos || []);
    } catch (e) {
      setError(e.message || 'Erro ao carregar conteúdos.');
    } finally {
      setLoading(false);
    }
  }, [pastaId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener('refreshPage', onRefresh);
    return () => window.removeEventListener('refreshPage', onRefresh);
  }, [load]);

  const canWrite = !!(pasta?.canWrite || setor?.canWrite);

  const openViewer = async (item) => {
    setViewer(item);
    try {
      await trilhaJson(`/api/trilha-aprendizado/conteudos/${item.id}/visualizar`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setConteudos((prev) =>
        prev.map((c) => (c.id === item.id ? { ...c, visualizado: true } : c))
      );
    } catch {
      /* visualização é best-effort */
    }
  };

  const handleAdd = async () => {
    if (!arquivo) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      fd.append('titulo', titulo.trim() || arquivo.name);
      fd.append('descricao', descricao.trim());
      if (tipo) fd.append('tipo', tipo);
      await trilhaJson(`/api/trilha-aprendizado/pastas/${pastaId}/conteudos`, {
        method: 'POST',
        body: fd,
      });
      setOpenAdd(false);
      setTitulo('');
      setDescricao('');
      setTipo('');
      setArquivo(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remover "${item.titulo}"?`)) return;
    try {
      await trilhaJson(`/api/trilha-aprendizado/conteudos/${item.id}`, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const renderViewer = () => {
    if (!viewer) return null;
    const url = mediaUrl(viewer.arquivoUrl);
    const t = viewer.tipo;
    if (t === 'video') {
      return (
        <Box component="video" src={url} controls autoPlay sx={{ width: '100%', maxHeight: '70vh', bgcolor: '#000' }} />
      );
    }
    if (t === 'imagem') {
      return <Box component="img" src={url} alt={viewer.titulo} sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />;
    }
    if (t === 'pdf') {
      return (
        <Box
          component="iframe"
          src={url}
          title={viewer.titulo}
          sx={{ width: '100%', height: '70vh', border: 0 }}
        />
      );
    }
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ mb: 2 }}>Pré-visualização não disponível para este tipo.</Typography>
        <Button variant="contained" href={url} target="_blank" rel="noopener noreferrer">
          Abrir / baixar arquivo
        </Button>
      </Box>
    );
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(setor?.id ? `/trilha-aprendizado/setores/${setor.id}` : '/trilha-aprendizado')}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Pastas
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#00695c' }}>
            {pasta?.nome || 'Pasta'}
          </Typography>
          {pasta?.observacao && (
            <Typography variant="body2" color="text.secondary">
              {pasta.observacao}
            </Typography>
          )}
        </Box>
        {canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)} sx={{ bgcolor: '#00897b' }}>
            Adicionar Conteúdo
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#00897b' }} />
        </Box>
      ) : conteudos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <VideocamIcon sx={{ fontSize: 56, color: '#80cbc4', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhum conteúdo ainda nesta pasta
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canWrite ? 'Adicione o primeiro vídeo, PDF ou imagem.' : 'Aguarde a publicação de conteúdos.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {conteudos.map((item) => {
            const color = TIPO_COLOR[item.tipo] || TIPO_COLOR.arquivo;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.06)',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    position: 'relative',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(0,0,0,0.1)' },
                  }}
                >
                  <CardActionArea onClick={() => openViewer(item)}>
                    <Box
                      sx={{
                        height: 120,
                        background: item.thumbnailUrl
                          ? `center/cover url(${mediaUrl(item.thumbnailUrl)})`
                          : `linear-gradient(135deg, ${color}22 0%, ${color}55 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color,
                      }}
                    >
                      {!item.thumbnailUrl && TIPO_ICON[item.tipo]}
                    </Box>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip size="small" label={item.tipo} sx={{ bgcolor: `${color}18`, color }} />
                        {item.visualizado && (
                          <Chip
                            size="small"
                            icon={<CheckCircleIcon />}
                            label="Assistido"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {item.titulo}
                      </Typography>
                      {item.descricao && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {item.descricao}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                  {canWrite && (
                    <IconButton
                      size="small"
                      color="error"
                      sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)' }}
                      onClick={() => handleDelete(item)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={openAdd} onClose={() => !saving && setOpenAdd(false)} fullWidth maxWidth="sm">
        <DialogTitle>Adicionar conteúdo</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} fullWidth />
          <TextField
            select
            label="Tipo (opcional — detectado pelo arquivo)"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            fullWidth
          >
            <MenuItem value="">Detectar automaticamente</MenuItem>
            <MenuItem value="video">Vídeo</MenuItem>
            <MenuItem value="imagem">Imagem</MenuItem>
            <MenuItem value="pdf">PDF</MenuItem>
            <MenuItem value="ppt">PPT</MenuItem>
          </TextField>
          <TextField
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Button variant="outlined" component="label">
            {arquivo ? arquivo.name : 'Selecionar arquivo (vídeo, imagem, PDF, PPT)'}
            <input
              hidden
              type="file"
              accept="video/*,image/*,.pdf,.ppt,.pptx,application/pdf"
              onChange={(e) => setArquivo(e.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving || !arquivo} sx={{ bgcolor: '#00897b' }}>
            {saving ? 'Enviando...' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewer} onClose={() => setViewer(null)} fullWidth maxWidth="lg">
        <DialogTitle>{viewer?.titulo}</DialogTitle>
        <DialogContent dividers>{renderViewer()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setViewer(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
